import { VMSVariationType } from '@dayetopia/types';
import { PAINKILLERS_ICON } from '@constants';
import {
  StepType,
  ScreeningHowToHelpData,
  TreatmentTypes,
  Article,
  BrandSpecialist,
  ResultParameterType,
  ResultEvaluationDetection,
  CustomArticleData,
  ReportSectionSelectorItem
} from '@contracts';

export const WISP_DAYE_LOGO = 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZpAx-B5LeNNTxDfl_WISPxDAYE.svg';
export const WISP_BLOG_IMAGE = 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/ZpDzAx5LeNNTxF4N_wisp-blog-image.svg';
export const WISP_LOGO = 'https://images.prismic.io/ecommerce-website/ZpApPR5LeNNTxDWR_WISP-LOGO.webp';
const WISP_TAB_FOOTER_NAV_ICON = 'https://images.prismic.io/ecommerce-website/ZpApPR5LeNNTxDWR_WISP-LOGO.webp';
export const WISP_SPECIALIST_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpApQh5LeNNTxDWW_WISP-SPECIALIST.webp';
export const WISP_PRODUCT_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpApQB5LeNNTxDWU_WISP-PRODUCT.webp';
const WISP_ESTRADIOL_IMAGE = 'https://images.prismic.io/ecommerce-website/Zsd5xUaF0TcGJR1G_WISP-ESTRADIOL_2.webp';
const WISP_RETEST_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpApPx5LeNNTxDWT_WISP-PREVENTION_2.webp';
const WISP_PREVENTION_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpApPh5LeNNTxDWS_WISP-PREVENTION.webp';
const WISP_VAGINAL_HEALTH_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpApQx5LeNNTxDWX_WISP-VHEALTH.webp';
const WISP_REPRODUCTIVE_CARE = 'https://images.prismic.io/ecommerce-website/ZpApQR5LeNNTxDWV_WISP-REPRODUCTIVE-CARE.webp';
const WISP_FERILITY_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpApPB5LeNNTxDWQ_WISP-FERTILITY.webp';
const WISP_BETTER_SEX_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpApOx5LeNNTxDWP_WISP-BETTER-SEX.webp';
export const WISP_PROBIOTICS_IMAGE = 'https://images.prismic.io/ecommerce-website/ZpAxZh5LeNNTxDeR_WISP-PROBIOTICS.webp';

export const WISP_DAYE_URL = 'https://hellowisp.com/daye-wisp?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye';
export const WISP_DAYE_PRODUCT_URL =
  'https://hellowisp.com/daye-wisp?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye';
export const WISP_PREVENTION_URL =
  'https://hellowisp.com/shop/prevention?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye';
export const WISP_PREVENTION_YEAST_URL =
  'https://hellowisp.com/shop/prevention?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye#yeast-infections';
export const WISP_PREVENTION_BV_URL =
  'https://hellowisp.com/shop/prevention?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye#bacterial-vaginosis';
export const WISP_ESTRADIOL_URL =
  'https://hellowisp.com/products/estradiol?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye';

export enum Brand {
  Wisp = 'retail_box_wisp',
  Boots = 'retail_box_boots'
}

const variationBrandMappings = {
  [VMSVariationType.RetailBoxWisp]: Brand.Wisp,
  [VMSVariationType.D2CBoxWisp]: Brand.Wisp,
  [VMSVariationType.RetailBox]: Brand.Boots
} as Record<VMSVariationType, Brand | undefined>;

export const useBrand = (reportVariation = VMSVariationType.TransportMedium): Brand | null => {
  return variationBrandMappings[reportVariation] ?? null;
};

export interface BrandContent {
  brandLogo?: string;
  proceedToNextStepsText?: string;
  footerNavIcon?: string;
  blogImage?: string;
  supportEmail: string;
  supportEmailSubject: string;
  supportEmailBody: string;
  replacementKitText: string;
  nurse?: BrandSpecialist;
  resourcesHeading?: string;
  resourcesSubheading?: string;
  steps?: {
    title: string;
    description: string;
    iconSrc: string;
    content?: StepType;
  }[];
}

const brandContent: Partial<Record<Brand, BrandContent>> = {
  [Brand.Wisp]: {
    brandLogo: WISP_DAYE_LOGO,
    proceedToNextStepsText: 'branding.wisp-proceed-to',
    footerNavIcon: WISP_TAB_FOOTER_NAV_ICON,
    blogImage: WISP_BLOG_IMAGE,
    supportEmailSubject: 'Vaginal Health Test: Replacement Kit Request',
    supportEmailBody:
      'Dear Wisp Customer Support,\n\nI am writing to request a replacement kit for my recent Vaginal Health Test.\n\nHere are my details:\nName: $customer_name\n\nKit ID of Original Test: $order_number\n\nThank you in advance!\n\nBest wishes,\n$customer_name',
    supportEmail: 'support@hellowisp.com',
    replacementKitText: 'branding.wisp-replacement-kit-text',
    resourcesHeading: 'branding.wisp-resources-heading',
    resourcesSubheading: 'branding.wisp-resources-subheading',
    nurse: {
      name: 'branding.wisp-connect-with-provider',
      picture: WISP_SPECIALIST_IMAGE,
      shortDescription: '',
      url: WISP_DAYE_URL,
      isPrepaid: false
    },
    steps: [
      {
        title: 'branding.wisp-specialist-title',
        description: 'branding.wisp-specialist-description',
        iconSrc: WISP_LOGO,
        content: StepType.SpecialistsWisp
      },
      {
        title: 'branding.wisp-prevention-title',
        description: 'branding.wisp-prevention-description',
        iconSrc: WISP_PREVENTION_IMAGE,
        content: StepType.PreventionWisp
      },
      {
        title: 'branding.wisp-retest-title',
        description: 'branding.wisp-retest-description',
        iconSrc: WISP_RETEST_IMAGE,
        content: StepType.RetestWisp
      }
    ]
  }
};

export const getBrandContent = (brand: Brand | null): BrandContent | null => {
  if (!brand) {
    return null;
  }
  return brandContent[brand] || null;
};

export const getScreeningSteps = (brand: Brand | null) => {
  if (!brand || !brandContent[brand]) {
    return null;
  }
  return brandContent[brand].steps || null;
};

const brandedProvioticsData: Partial<Record<Brand, ScreeningHowToHelpData>> = {
  [Brand.Wisp]: {
    title: 'branding.wisp-proviotics-section-title',
    sections: [
      {
        description: 'branding.wisp-probiotics-section-text-01',
        brandedProduct: {
          title: 'branding.wisp-equalizing-probiotics',
          subtitle: 'branding.wisp-equalizing-probiotics-description',
          link: 'https://hellowisp.com/products/equalizing-probiotics?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
          icon: WISP_PROBIOTICS_IMAGE
        }
      }
    ]
  }
};

export const getBrandedProvioticsData = (brand: Brand | null): ScreeningHowToHelpData | null => {
  if (!brand) {
    return null;
  }
  return brandedProvioticsData[brand] || null;
};

export interface BrandedTreatment {
  title: string;
  subtitle: string;
  link: string;
  icon: string;
  background?: string;
}

export type BrandedTreatmentsData = {
  [key in Brand]: {
    [treatmentType in TreatmentTypes]?: BrandedTreatment;
  };
};

const brandedTreatmentsData: BrandedTreatmentsData = {
  [Brand.Wisp]: {
    [TreatmentTypes.Thrush]: {
      title: 'Yeast Infection Treatment',
      subtitle: '',
      link: 'https://hellowisp.com/products/yeast-antifungals?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
      icon: WISP_PRODUCT_IMAGE
    },
    [TreatmentTypes.MycoplasmaHominis]: {
      title: 'M. Hominis Treatment',
      subtitle: '',
      link: 'https://hellowisp.com/products/mhominis?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
      icon: WISP_PRODUCT_IMAGE
    },
    [TreatmentTypes.Bv]: {
      title: 'Bacterial Vaginosis Treatment',
      subtitle: '',
      link: 'https://hellowisp.com/products/bv-antibiotics?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
      icon: WISP_PRODUCT_IMAGE
    },
    [TreatmentTypes.Ureaplasma]: {
      title: 'Ureaplasma Treatment',
      subtitle: '',
      link: 'https://hellowisp.com/products/ureaplasma?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
      icon: WISP_PRODUCT_IMAGE
    }
  },
  [Brand.Boots]: {}
};

export const getBrandedTreatmentsData = (brand: Brand | null): BrandedTreatmentsData[Brand] | null => {
  if (!brand) {
    return null;
  }
  return brandedTreatmentsData[brand] || null;
};

export interface CustomBrandedProduct {
  title: string;
  subtitle: string;
  link: string;
  icon: string;
  background?: string;
}

export type CustomBrandedProductsData = {
  [key in Brand]?: {
    [productName: string]: CustomBrandedProduct;
  };
};

const brandedProductsData: CustomBrandedProductsData = {
  [Brand.Wisp]: {
    Estradiol: {
      title: 'Estradiol Vaginal Cream',
      subtitle: '',
      link: WISP_ESTRADIOL_URL,
      icon: WISP_ESTRADIOL_IMAGE
    }
  }
};

export type BrandedHowToHelpOverrides = Partial<{
  [key in Brand]: Partial<{
    [type in ResultParameterType]: Partial<{
      [data in ResultEvaluationDetection]: ScreeningHowToHelpData;
    }>;
  }>;
}>;

const howToHelpTitle = 'screening.how-we-can-help';
const brandedHowToHelpOverrides: BrandedHowToHelpOverrides = {
  [Brand.Wisp]: {
    [ResultParameterType.ANAEROBIC_MICROORGANISMS]: {
      [ResultEvaluationDetection.DetectedAbnormal]: {
        title: howToHelpTitle,
        hasNextStepsButton: true,
        sections: [
          {
            description: 'symptom.anaerobic-bacteria-how-we-help-1',
            treatmentTypeIds: [TreatmentTypes.Bv]
          },
          {
            description: 'symptom.anaerobic-bacteria-how-we-help-2',
            brandedProduct: brandedProvioticsData[Brand.Wisp]?.sections?.[0]?.brandedProduct
          },
          {
            description: 'symptom.anaerobic-bacteria-how-we-help-3',
            brandedSpecialists: brandContent[Brand.Wisp]?.nurse ? [brandContent[Brand.Wisp].nurse] : null
          }
        ]
      }
    },
    [ResultParameterType.LACTOBACILLUS]: {
      [ResultEvaluationDetection.Depleted]: {
        title: howToHelpTitle,
        hasNextStepsButton: true,
        sections: [
          {
            description: 'branding.wisp-we-recommend-call',
            brandedSpecialists: brandContent[Brand.Wisp]?.nurse ? [brandContent[Brand.Wisp].nurse] : null
          },
          {
            description: 'branding.wisp-estradiol-text-lactobacilli',
            brandedProduct: brandedProductsData[Brand.Wisp]?.Estradiol
          },
          {
            description: 'symptom.lactobacillus-how-we-help',
            brandedProduct: brandedProvioticsData[Brand.Wisp]?.sections?.[0]?.brandedProduct
          }
        ]
      }
    },
    [ResultParameterType.CANDIDA]: {
      [ResultEvaluationDetection.DetectedAbnormal]: {
        title: howToHelpTitle,
        hasNextStepsButton: true,
        sections: [
          {
            heading: 'components.consultations',
            description: 'symptom.consult-specialist-or-gp',
            brandedSpecialists: brandContent[Brand.Wisp]?.nurse ? [brandContent[Brand.Wisp].nurse] : null
          },
          {
            description: 'branding.wisp-estradiol-text-candida',
            brandedProduct: brandedProductsData[Brand.Wisp]?.Estradiol
          },
          {
            heading: 'components.treatments',
            description: 'symptom.explore-microbiome-treatment-options',
            treatmentTypeIds: [TreatmentTypes.Thrush],
            hasProviotics: true
          }
        ]
      }
    },
    [ResultParameterType.UREAPLASMA]: {
      [ResultEvaluationDetection.DetectedAbnormal]: {
        title: howToHelpTitle,
        hasNextStepsButton: true,
        sections: [
          {
            heading: 'branding.wisp-meet-nurses',
            description: 'branding.wisp-meet-nurses-action-plan',
            brandedSpecialists: brandContent[Brand.Wisp]?.nurse ? [brandContent[Brand.Wisp].nurse] : null
          },
          {
            heading: 'components.treatments',
            description: 'symptom.ureaplasma-how-we-help',
            hasProviotics: false,
            brandedProduct: brandedTreatmentsData[Brand.Wisp]?.[TreatmentTypes.Ureaplasma]
          },
          brandedProvioticsData[Brand.Wisp]?.sections?.[0] || {}
        ]
      }
    }
  }
};

export const getBrandedHowToHelpOverrides = (brand: Brand | null): BrandedHowToHelpOverrides[Brand] | null => {
  if (!brand) {
    return null;
  }
  return brandedHowToHelpOverrides[brand] || null;
};

const brandedDealSections: Partial<Record<Brand, ReportSectionSelectorItem[]>> = {
  [Brand.Wisp]: [
    {
      id: 'wisp-treatments',
      title: 'Treatments',
      icon: PAINKILLERS_ICON,
      smallIcon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/901f8589-5e49-4421-99f9-b55b5d986244_Pain+Relief+Meds+Small.svg',
      deals: [
        {
          title: 'Vaginal & STI treatment',
          subtitle: 'Prescription treatments for Yeast, BV, mycoplasma & ureaplasma',
          link: 'https://hellowisp.com/shop/vaginal-health?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
          image: WISP_VAGINAL_HEALTH_IMAGE
        },
        {
          title: 'Infection prevention',
          subtitle: 'Defense against vaginal infections, backed by science',
          link: 'https://hellowisp.com/shop/prevention?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
          image: WISP_PREVENTION_IMAGE
        }
      ]
    },
    {
      id: 'wisp-reproductive-care',
      title: 'Reproductive care',
      icon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/68183d40-4546-4405-b3ca-cfad52bb0122_sex-40x40px.svg',
      smallIcon: 'https://ecommerce-website.cdn.prismic.io/ecommerce-website/91147427-b5dc-4917-b59b-f36cd8ca305f_sex-20x20px.svg',
      deals: [
        {
          title: 'Fertility',
          subtitle: 'Education, services, and resources to start your journey',
          link: 'https://hellowisp.com/shop/fertility?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
          image: WISP_FERILITY_IMAGE
        },
        {
          title: 'Better sex',
          subtitle: 'Extra support between the sheets',
          link: 'https://hellowisp.com/shop/wellness-essentials/better-sex?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
          image: WISP_BETTER_SEX_IMAGE
        },
        {
          title: 'Contraception',
          subtitle: 'Birth control, emergency contraception, and more',
          link: 'https://hellowisp.com/shop/reproductive-health?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye',
          image: WISP_REPRODUCTIVE_CARE
        }
      ]
    }
  ]
};

export const getBrandedDealSections = (brand: Brand | null): ReportSectionSelectorItem[] | null => {
  if (!brand) {
    return null;
  }
  return brandedDealSections[brand] || null;
};

export interface BrandedReferralBanner {
  title: string;
  subtitle?: string;
  code: string;
  icon: string;
  externalLink?: string;
  buttonText?: string;
}

const brandedReferralBanner: Partial<Record<Brand, BrandedReferralBanner>> = {
  [Brand.Wisp]: {
    title: 'branding.wisp-dayecode-title',
    subtitle: 'branding.wisp-dayecode-subtitle',
    code: 'DAYE20',
    icon: WISP_RETEST_IMAGE,
    buttonText: 'Explore offers on Wisp',
    externalLink: WISP_DAYE_URL
  }
};

export const getBrandedReferralBanner = (brand: Brand | null): BrandedReferralBanner | null => {
  if (!brand) {
    return null;
  }
  return brandedReferralBanner[brand] || null;
};

const brandedArticlesData: Partial<Record<Brand, Article<CustomArticleData>[]>> = {
  [Brand.Wisp]: [
    {
      id: '1',
      data: {
        featured_image: {
          url: 'https://images.prismic.io/ecommerce-website/ZpDwvB5LeNNTxF2s_wisp-blog-01.webp'
        },
        title: 'How to Improve Your Vaginal Microbiome',
        custom_url:
          'https://hellowisp.com/blog/how-to-improve-your-vaginal-microbiome?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye'
      }
    },
    {
      id: '2',
      data: {
        featured_image: {
          url: 'https://images.prismic.io/ecommerce-website/ZpDxTB5LeNNTxF3G_wisp-blog-02.webp'
        },
        title: 'How to Stop Recurrent Infections With Your Vaginal Microbiome',
        custom_url:
          'https://hellowisp.com/blog/how-to-stop-recurrent-infections-with-your-vaginal-microbiome?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye'
      }
    },
    {
      id: '3',
      data: {
        featured_image: {
          url: 'https://images.prismic.io/ecommerce-website/ZpDxuR5LeNNTxF3n_wisp-blog-03.webp'
        },
        title: 'The Best Probiotics for Vaginal Health',
        custom_url:
          'https://hellowisp.com/blog/the-best-probiotics-for-vaginal-health?utm_source=daye&utm_medium=brandpartnership&utm_campaign=campaign&utm_term=daye'
      }
    }
  ]
};

export const getBrandedArticlesData = (brand: Brand | null): Article<CustomArticleData>[] | null => {
  if (!brand) {
    return null;
  }
  return brandedArticlesData[brand] || null;
};
